import * as React from 'react';
import { Dialog } from '@headlessui/react';
import { SaleTerm, SubscriptionType } from 'constants/payments';
import type { MemberOfferSubscription } from 'constants/payloads/memberOffers';
import { getMemberSaleStrip } from 'components/MemberOffers/memberOfferCopy';
import { SaleBannerView } from 'components/MemberOffers/SaleBanner';
import type { MemberBannerPreviewConfig } from 'components/MemberOffers/bannerPreview';

interface Props {
  config: MemberBannerPreviewConfig;
}

/** Does not touch the live offers provider, billing API, campaign telemetry or dismissal storage. */
export default function MemberBannerPreview({ config }: Props) {
  const [endsAt] = React.useState(() => Date.now() + 2 * 24 * 60 * 60 * 1000);
  const [dismissed, setDismissed] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const row: MemberOfferSubscription = {
    id: 'preview-only',
    product: SubscriptionType.DashboardStandard,
    productName: 'Dashboard',
    currentTerm: SaleTerm.Monthly,
    terms: [SaleTerm.Annual, SaleTerm.Lifetime],
    currency: 'usd',
    currentAnnualCents: 90000,
    sale:
      config.variant === 'annual'
        ? {
            id: 'preview-only',
            title: 'Black Friday member sale',
            endsAt,
            percentOff: config.percentOff,
            duration: config.duration,
            durationMonths: config.duration === 'repeating' ? 12 : null,
          }
        : null,
    annual: {
      firstTermCents: 66000 * (1 - config.percentOff / 100),
      renewalCents: config.duration === 'forever' ? 66000 * (1 - config.percentOff / 100) : 66000,
      listCents: 66000,
      duration: config.duration,
      durationMonths: config.duration === 'repeating' ? 12 : null,
      percentOff: config.percentOff,
      keepsExisting: false,
    },
    lifetime: {
      priceCents: config.variant === 'lifetime' ? config.lifetimePriceCents : 150000,
      listCents: 150000,
      sale:
        config.variant === 'lifetime'
          ? {
              id: 'preview-only',
              title: 'Lifetime member sale',
              endsAt,
              priceCents: config.lifetimePriceCents,
              listCents: 150000,
            }
          : null,
    },
  };
  const strip = getMemberSaleStrip(row);
  return (
    <>
      {config.variant !== 'off' && !dismissed && strip && (
        <SaleBannerView
          strip={strip}
          endsAt={endsAt}
          clockOffset={0}
          isPreview
          onOpen={() => {
            setDismissed(true);
            setOpen(true);
          }}
          onDismiss={() => setDismissed(true)}
        />
      )}
      <Dialog open={open} onClose={() => setOpen(false)} className="relative z-[110]">
        <div className="fixed inset-0 bg-black/80" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-2xl border border-emerald-300/30 bg-[#070a09] p-6 text-white">
            <Dialog.Title className="text-lg font-semibold">Banner preview</Dialog.Title>
            <p className="mt-3 text-sm text-white/75">
              This is a sample sale for checking the page layout. It does not change your plan or
              start a payment. Reload this address to show the banner again.
            </p>
            <button
              type="button"
              className="mt-5 rounded-lg bg-emerald-300 px-4 py-2 font-semibold text-[#062012]"
              onClick={() => setOpen(false)}
            >
              Close preview
            </button>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
